/**
 * Role context. Authority comes from Azure Static Web Apps built-in auth
 * (`/.auth/me` → clientPrincipal → app roles). In standalone/tabletop mode
 * (no SWA auth configured) the user picks an *acting role* locally so the POC
 * is fully usable offline — clearly labelled as standalone, never presented as
 * a real sign-in.
 *
 * Roles gate *actions* only. Reading is never gated — the tool is advisory and
 * transparency is a feature.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  can as roleCan,
  roleFromPrincipal,
  ROLE_LABELS,
  OFFICER_ROLES,
  OBSERVER_ROLE,
  type Role,
  type Capability,
  type ClientPrincipal,
} from '@duty/shared';

const ACTING_ROLE_KEY = 'duty.actingRole.v1';

type Source = 'swa' | 'standalone';

interface RoleValue {
  role: Role;
  /** Display name of the signed-in principal, if authenticated via SWA. */
  userName: string | null;
  /** Where the role came from: real SWA auth, or a local standalone choice. */
  source: Source;
  /** True once the initial `/.auth/me` probe has resolved. */
  ready: boolean;
  can: (capability: Capability) => boolean;
  /** Set the acting role in standalone mode (ignored when SWA-authenticated). */
  setActingRole: (role: Role) => void;
}

const RoleContext = createContext<RoleValue | null>(null);

function loadActingRole(): Role {
  try {
    const raw = window.localStorage.getItem(ACTING_ROLE_KEY);
    if (raw && (OFFICER_ROLES as readonly string[]).includes(raw)) return raw as Role;
  } catch {
    /* storage unavailable */
  }
  // Default standalone role: duty officer (the primary operator in the POC).
  return 'duty-officer';
}

export function RoleProvider({ children }: { children: ReactNode }) {
  const [principal, setPrincipal] = useState<ClientPrincipal | null>(null);
  const [source, setSource] = useState<Source>('standalone');
  const [ready, setReady] = useState(false);
  const [actingRole, setActingRoleState] = useState<Role>(loadActingRole);

  // Probe SWA auth once. A configured SWA returns { clientPrincipal }. When auth
  // isn't configured (local dev / standalone) the endpoint 404s or returns null;
  // we fall back to the locally chosen acting role.
  useEffect(() => {
    const ctrl = new AbortController();
    fetch('/.auth/me', { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { clientPrincipal?: ClientPrincipal | null } | null) => {
        const cp = data?.clientPrincipal ?? null;
        if (cp && roleFromPrincipal(cp) !== OBSERVER_ROLE) {
          setPrincipal(cp);
          setSource('swa');
        }
      })
      .catch(() => {
        /* standalone — keep the acting role */
      })
      .finally(() => setReady(true));
    return () => ctrl.abort();
  }, []);

  const setActingRole = (role: Role) => {
    setActingRoleState(role);
    try {
      window.localStorage.setItem(ACTING_ROLE_KEY, role);
    } catch {
      /* ignore */
    }
  };

  const role: Role = source === 'swa' ? roleFromPrincipal(principal) : actingRole;

  const value = useMemo<RoleValue>(
    () => ({
      role,
      userName: source === 'swa' ? principal?.userDetails ?? ROLE_LABELS[role] : null,
      source,
      ready,
      can: (c) => roleCan(role, c),
      setActingRole,
    }),
    [role, source, principal, ready],
  );

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}

export function useRole(): RoleValue {
  const ctx = useContext(RoleContext);
  if (!ctx) throw new Error('useRole must be used within RoleProvider');
  return ctx;
}
